import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Download, CheckCircle, Clock, XCircle, Plus, FileCheck, IdCard, FileText, Award, ShieldCheck, Inbox, FolderOpen } from 'lucide-react'
import { PortalApplySheet } from './PortalApplySheet'
import { JournalistScoreCard } from './JournalistScoreCard'
import { AssociationOfficerPanel } from './AssociationOfficerPanel'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { PortalStatCard } from './ui/PortalStatCard'
import { PortalEmptyState } from './ui/PortalEmptyState'
import { PortalSkeletonGrid, PortalSkeletonStat } from './ui/PortalSkeleton'

const STATUS_ICONS: Record<string, React.ReactNode> = {
  active:  <CheckCircle className="w-4 h-4 text-success" />,
  pending: <Clock className="w-4 h-4 text-warning" />,
  rejected:<XCircle className="w-4 h-4 text-destructive" />,
  revoked: <XCircle className="w-4 h-4 text-muted-foreground" />,
}

const STATUS_COLORS: Record<string, string> = {
  active:  'bg-success/15 text-success',
  pending: 'bg-warning/15 text-warning',
  rejected:'bg-destructive/15 text-destructive',
  revoked: 'bg-muted text-muted-foreground',
}

export function PortalDashboardView() {
  const navigate = useNavigate()
  const [applyOpen, setApplyOpen] = useState(false)

  const { data: profile } = useQuery({
    queryKey: ['portal-dashboard-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_profiles')
        .select('full_name, avatar_url, phone, nationality, bio, outlet_name, outlet_logo_url')
        .eq('id', user.id)
        .maybeSingle() as any)
      return data as {
        full_name: string; avatar_url?: string; phone?: string; nationality?: string
        bio?: string; outlet_name?: string; outlet_logo_url?: string
      } | null
    },
  })

  const PROFILE_CHECKLIST: { key: keyof NonNullable<typeof profile>; label: string }[] = [
    { key: 'full_name', label: 'Full name' },
    { key: 'phone', label: 'Phone number' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'bio', label: 'Short bio' },
    { key: 'outlet_name', label: 'Outlet / organisation name' },
    { key: 'outlet_logo_url', label: 'Outlet logo' },
  ]
  const missingFields = profile ? PROFILE_CHECKLIST.filter(f => !profile[f.key]) : []
  const profileComplete = !!profile && missingFields.length === 0

  // Bridge to the org-side CRM record (Media Registry), matched by email —
  // surfaces the photo/position/verification ID an organisation has on file,
  // and is what makes "My Passes" resolve to anything.
  const { data: crmProfile } = useQuery({
    queryKey: ['portal-crm-bridge'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) return null
      const { data } = await (supabase
        .from('journalists' as any)
        .select('id, photo_url, position, verification_id, organizations(name)')
        .eq('email', user.email)
        .maybeSingle() as any)
      return data as any | null
    },
  })

  const { data: activePassCount } = useQuery({
    queryKey: ['portal-active-pass-count', crmProfile?.id],
    enabled: !!crmProfile?.id,
    queryFn: async () => {
      const { count } = await (supabase
        .from('press_passes' as any)
        .select('id', { count: 'exact', head: true })
        .eq('journalist_id', crmProfile.id)
        .eq('status', 'active') as any)
      return count ?? 0
    },
  })

  const { data: score } = useQuery({
    queryKey: ['portal-dashboard-score'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_coverage_scores' as any)
        .select('score_total')
        .eq('journalist_id', user.id)
        .maybeSingle() as any)
      return data as { score_total: number } | null
    },
  })

  const { data: accreditations } = useQuery({
    queryKey: ['my-accreditations'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await (supabase
        .from('journalist_accreditations' as any)
        .select(`
          id, status, accredited_at, created_at,
          organizations(id, name, brand_primary, brand_accent, logo_url)
        `)
        .eq('journalist_id', user.id) as any)
      return (data ?? []) as any[]
    }
  })

  const { data: submissions } = useQuery({
    queryKey: ['my-submissions'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await supabase
        .from('journalist_submissions')
        .select('id, content_url, submitted_at, similarity_score, organizations(name)')
        .eq('journalist_id', user.id)
        .order('submitted_at', { ascending: false })
        .limit(5)
      return (data ?? []) as any[]
    }
  })

  const { data: submissionsCount } = useQuery({
    queryKey: ['my-submissions-count'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return 0
      const { count } = await supabase
        .from('journalist_submissions')
        .select('id', { count: 'exact', head: true })
        .eq('journalist_id', user.id)
      return count ?? 0
    }
  })

  const { data: myLeadershipPositions } = useQuery({
    queryKey: ['portal-my-leadership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await (supabase
        .from('association_leadership' as any)
        .select(`
          id, tenure_start, association_id, member_id,
          association_positions(title, permissions),
          associations(name)
        `)
        .eq('user_id', user.id)
        .eq('is_current', true) as any)
      return (data || []) as any[]
    },
  })

  const { data: orgLanguages } = useQuery({
    queryKey: ['accredited-org-langs', (accreditations ?? []).map((a: any) => a.organizations?.id)],
    enabled: (accreditations ?? []).length > 0,
    queryFn: async () => {
      const orgIds = (accreditations ?? []).map((a: any) => a.organizations?.id).filter(Boolean)
      if (!orgIds.length) return {}
      const { data } = await (supabase
        .from('org_languages' as any)
        .select('org_id, language_code, org_media_kits(storage_path)')
        .in('org_id', orgIds)
        .eq('is_active', true) as any)
      const map: Record<string, any[]> = {}
      ;(data ?? []).forEach((l: any) => {
        if (!map[l.org_id]) map[l.org_id] = []
        map[l.org_id].push(l)
      })
      return map
    }
  })

  const downloadKit = async (path: string) => {
    const { data } = await supabase.storage.from('media-kits').createSignedUrl(path, 60)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  const LANG_NAMES: Record<string, string> = { en: 'EN', fr: 'FR', pt: 'PT' }

  const statsLoading = accreditations === undefined || submissionsCount === undefined

  return (
    <div className="space-y-8">
      <PortalPageHeader
        title="My Dashboard"
        description="Your accreditations, passes, and content submissions at a glance."
      />

      {/* Identity */}
      <Card>
        <CardContent className="pt-6 flex items-center gap-4">
          <Avatar className="w-14 h-14 border-2 border-border">
            <AvatarImage src={crmProfile?.photo_url ?? profile?.avatar_url ?? undefined} alt={profile?.full_name} />
            <AvatarFallback>
              {(profile?.full_name ?? '?')
                .split(' ')
                .map((p: string) => p[0])
                .slice(0, 2)
                .join('')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-justify font-semibold text-lg truncate">{profile?.full_name ?? '…'}</p>
            {crmProfile ? (
              <>
                <p className="text-justify text-sm text-muted-foreground">
                  {crmProfile.position || 'Journalist'}
                  {crmProfile.organizations?.name ? ` · ${crmProfile.organizations.name}` : ''}
                </p>
                {crmProfile.verification_id && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-success" />
                    <span className="text-xs font-mono text-muted-foreground">{crmProfile.verification_id}</span>
                  </div>
                )}
              </>
            ) : (
              <p className="text-justify text-sm text-muted-foreground">
                Not yet linked to an organisation's media registry. Once an institution accredits you, your
                verified details and press passes will appear here.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stat row */}
      {statsLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <PortalSkeletonStat key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <PortalStatCard
            icon={FileCheck}
            label="Accreditations"
            value={(accreditations ?? []).filter((a: any) => a.status === 'active').length}
            tone="success"
          />
          <PortalStatCard icon={IdCard} label="Active Passes" value={activePassCount ?? 0} />
          <PortalStatCard icon={FileText} label="Submissions" value={submissionsCount ?? 0} />
          <PortalStatCard icon={Award} label="Coverage Score" value={Math.round(score?.score_total ?? 0)} tone="accent" />
        </div>
      )}

      {/* Score card */}
      <div className="max-w-sm">
        <JournalistScoreCard />
      </div>

      {/* Association officer panels, shown when journalist holds a leadership position */}
      {(myLeadershipPositions || []).length > 0 && (
        <section className="space-y-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">Your Association Roles</h2>
            <p className="text-justify text-sm text-muted-foreground">Manage your responsibilities as an association officer.</p>
          </div>
          {(myLeadershipPositions || []).map((pos: any) => (
            <AssociationOfficerPanel
              key={pos.id}
              associationId={pos.association_id}
              associationName={pos.associations?.name ?? ''}
              memberId={pos.member_id}
              positionTitle={pos.association_positions?.title ?? ''}
              tenureStart={pos.tenure_start}
              permissions={pos.association_positions?.permissions ?? {}}
            />
          ))}
        </section>
      )}

      {/* Accreditations */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-foreground">My Accreditations</h2>
          <Button
            size="sm"
            disabled={!profileComplete}
            onClick={() => setApplyOpen(true)}
            className="bg-accent hover:bg-accent/90 text-accent-foreground gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            Apply
          </Button>
        </div>

        {profile && !profileComplete && (
          <div className="mb-4 rounded-lg border border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.08)] p-3 text-sm">
            <p className="text-justify text-warning font-medium">Complete your profile to apply for accreditation</p>
            <p className="text-justify text-xs text-warning/80 mt-1">
              Missing: {missingFields.map(f => f.label).join(', ')}.{' '}
              <button className="underline font-medium" onClick={() => navigate('profile')}>Complete profile</button>
            </p>
          </div>
        )}

        {accreditations === undefined ? (
          <PortalSkeletonGrid cards={3} />
        ) : accreditations.length === 0 ? (
          <PortalEmptyState
            icon={FolderOpen}
            message={profileComplete
              ? "No accreditations yet. Contact an organisation's Communications Officer, or apply to one directly."
              : "Complete your profile above before applying for accreditation."}
            actionLabel={profileComplete ? "Apply for accreditation" : undefined}
            onAction={profileComplete ? () => setApplyOpen(true) : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(accreditations ?? []).map((acc: any) => {
              const org = acc.organizations
              const langs = orgLanguages?.[org?.id] ?? []
              return (
                <Card key={acc.id} className="overflow-hidden">
                  <div
                    className="h-2"
                    style={{ backgroundColor: org?.brand_accent ?? 'hsl(var(--accent))' }}
                  />
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <OrgLogo name={org?.name} logoUrl={org?.logo_url} brandColor={org?.brand_primary} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{org?.name}</div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {STATUS_ICONS[acc.status]}
                          <Badge className={cn('text-[10px]', STATUS_COLORS[acc.status] ?? STATUS_COLORS.pending)}>
                            {acc.status}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <p className="text-justify text-xs text-muted-foreground">
                      Submitted: {new Date(acc.created_at).toLocaleDateString()}
                    </p>
                    {acc.status === 'active' && acc.accredited_at && (
                      <p className="text-justify text-xs text-muted-foreground">
                        Accredited: {new Date(acc.accredited_at).toLocaleDateString()}
                      </p>
                    )}
                    {acc.status === 'pending' && (
                      <p className="text-justify text-xs text-warning">
                        Awaiting review by {org?.name ?? 'the institution'}
                      </p>
                    )}

                    {langs.length > 0 && (
                      <div>
                        <p className="text-justify text-xs text-muted-foreground mb-1.5">Media Kits</p>
                        <div className="flex flex-wrap gap-1.5">
                          {langs.map((lang: any) => {
                            const kit = lang.org_media_kits?.[0]
                            return kit ? (
                              <Button
                                key={lang.language_code}
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs gap-1"
                                onClick={() => downloadKit(kit.storage_path)}
                              >
                                <Download className="w-3 h-3" />
                                {LANG_NAMES[lang.language_code] ?? lang.language_code.toUpperCase()}
                              </Button>
                            ) : null
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </section>

      <PortalApplySheet
        open={applyOpen}
        onOpenChange={setApplyOpen}
        existingOrgIds={(accreditations ?? []).map((a: any) => a.organizations?.id).filter(Boolean)}
      />

      {/* Recent submissions */}
      <section>
        <h2 className="text-base font-semibold text-foreground mb-4">Recent Submissions</h2>
        {submissions === undefined ? (
          <PortalSkeletonGrid cards={2} />
        ) : submissions.length === 0 ? (
          <PortalEmptyState
            icon={Inbox}
            message="You haven't submitted any content yet. Submit your published work to start building your coverage score."
            actionLabel="Submit content"
            onAction={() => navigate('submit')}
          />
        ) : (
          <div className="space-y-2">
            {submissions.map((sub: any) => (
              <div key={sub.id} className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border text-sm hover:shadow-md transition-shadow">
                <div className="flex-1 min-w-0">
                  <a href={sub.content_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate block">
                    {sub.content_url}
                  </a>
                  <span className="text-xs text-muted-foreground">{sub.organizations?.name} · {new Date(sub.submitted_at).toLocaleDateString()}</span>
                </div>
                {sub.similarity_score != null && (
                  <Badge variant="outline" className="text-xs shrink-0">
                    Originality: {Math.round((1 - sub.similarity_score) * 100)}%
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function cn(...classes: (string | undefined | false)[]) {
  return classes.filter(Boolean).join(' ')
}

function OrgLogo({ name, logoUrl, brandColor }: { name?: string; logoUrl?: string | null; brandColor?: string | null }) {
  const [failed, setFailed] = useState(false)
  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt={`${name ?? 'Organisation'} logo`}
        className="w-10 h-10 rounded-lg object-contain bg-card border border-border shrink-0"
        onError={() => setFailed(true)}
      />
    )
  }
  return (
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center text-primary-foreground font-bold text-sm shrink-0"
      style={{ backgroundColor: brandColor ?? 'hsl(var(--foreground))' }}
    >
      {name?.[0] ?? '?'}
    </div>
  )
}
